import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useImperativeHandle, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Button, Card, Col, DatePicker, Form, Input, Row } from "antd";
import CustomerSelect from "../../customers/CustomerSelect";
import TimezoneSelect from "../../../components/TimezoneSelect";
import CustomerLabelWithHelpPopover from "../../customers/CustomerLabelWithHelpPopover";
import ConsignmentRequirementsFormItems from "../ConsignmentRequirementsFormItems";
import ConsignmentCommunicationChannelsFormItems from "../ConsignmentCommunicationChannelsFormItems";
import DivisionAccessFormItem from "../../divisions/DivisionAccessFormItem";
import DateRangePicker from "../../../components/DateRangePicker";
import CustomerDeliveryInstructionsFormItem from "../../customers/CustomerDeliveryInstructionsFormItem";
const CoreDetailsStep = ({ active, tenantId, defaultTimezoneName, cloneConsignment, tenantSettings, onComplete }, exportedRef) => {
    const [form] = Form.useForm();
    const [selectedCustomer, setSelectedCustomer] = useState(undefined);
    useImperativeHandle(exportedRef, () => ({
        resetFields: () => form.resetFields(),
        getFieldsValue: () => form.getFieldsValue(true)
    }));
    useEffect(() => {
        if (cloneConsignment) {
            const pickupFromTo = (cloneConsignment.pickupFrom && cloneConsignment.pickupTo)
                ? [dayjs(cloneConsignment.pickupFrom), dayjs(cloneConsignment.pickupTo)]
                : null;
            const deliveryFromTo = (cloneConsignment.deliveryFrom && cloneConsignment.deliveryTo)
                ? [dayjs(cloneConsignment.deliveryFrom), dayjs(cloneConsignment.deliveryTo)]
                : null;
            form.setFieldsValue({
                'customerId': cloneConsignment.customer?.id,
                'salesOrderRef': cloneConsignment.salesOrderRef,
                'timezoneName': cloneConsignment.timezoneName,
                'orderDate': (cloneConsignment.orderDate)
                    ? dayjs(cloneConsignment.orderDate, 'YYYY-MM-DD')
                    : dayjs(),
                'customerRef': cloneConsignment.customerRef,
                'divisionId': cloneConsignment.division?.id,
                'pickupDateTimeRange': pickupFromTo,
                'deliveryDateTimeRange': deliveryFromTo,
                'instructions': cloneConsignment.instructions,
                'specialRequirements': (cloneConsignment.specialRequirements || []).map(it => it.id),
                'pickUpPhotoRequired': cloneConsignment.pickUpPhotoRequired,
                'completionPhotoRequired': cloneConsignment.completionPhotoRequired,
                'completionSignatureRequired': cloneConsignment.completionSignatureRequired,
                'scheduledCommunicationChannels': cloneConsignment.scheduledCommunicationChannels,
                'startedCommunicationChannels': cloneConsignment.startedCommunicationChannels,
                'completedCommunicationChannels': cloneConsignment.scheduledCommunicationChannels,
            });
        }
        else {
            let notificationChannels = [];
            if (tenantSettings.deliveryNotifications.enabled) {
                if (tenantSettings.deliveryNotifications.sendSMS) {
                    notificationChannels.push('SMS');
                }
                if (tenantSettings.deliveryNotifications.sendEmail) {
                    notificationChannels.push('EMAIL');
                }
            }
            form.setFieldsValue({
                'pickUpPhotoRequired': tenantSettings.consignmentDefaults.pickupPhotoRequired,
                'completionPhotoRequired': tenantSettings.consignmentDefaults.deliveryPhotoRequired,
                'completionSignatureRequired': tenantSettings.consignmentDefaults.deliverySignatureRequired,
                'scheduledCommunicationChannels': notificationChannels,
                'startedCommunicationChannels': notificationChannels,
                'completedCommunicationChannels': notificationChannels,
            });
        }
    }, [cloneConsignment]);
    const onFinishFailed = ({ errorFields }) => {
        form.scrollToField(errorFields[0].name);
    };
    const validatePickupBeforeDelivery = (rule, pickupRange) => {
        //const pickupRange = form.getFieldValue("pickupDateTimeRange");
        const deliveryRange = form.getFieldValue("deliveryDateTimeRange");
        if (deliveryRange && deliveryRange.length && pickupRange && pickupRange.length) {
            const pickupFrom = pickupRange[0];
            const deliveryFrom = deliveryRange[0];
            if (pickupFrom.isSameOrAfter(deliveryFrom)) {
                return Promise.reject(new Error('Pickup should be before delivery date'));
            }
        }
        return Promise.resolve();
    };
    if (!active || !tenantId) {
        return null;
    }
    return (_jsxs("div", { children: [_jsx(Form, { layout: "vertical", form: form, onFinish: onComplete, onFinishFailed: onFinishFailed, children: _jsxs(Row, { gutter: 10, className: "no-margin", children: [" ", _jsx(Col, { lg: 12, xl: 8, children: _jsxs(Card, { title: "Order", type: "inner", children: [_jsx(Form.Item, { name: "customerId", label: _jsx(CustomerLabelWithHelpPopover, {}), children: _jsx(CustomerSelect, { allowClear: true, tenantId: tenantId, onCustomerSelected: setSelectedCustomer }) }), _jsx(Form.Item, { name: "salesOrderRef", label: "Sales Order Reference", rules: [{
                                                max: 20, message: 'Please limit to 20 characters.'
                                            }], children: _jsx(Input, { maxLength: 20 }) }), _jsx(Form.Item, { name: "timezoneName", label: "Timezone", initialValue: defaultTimezoneName, rules: [{
                                                required: true, message: 'Please select a timezone.'
                                            }], children: _jsx(TimezoneSelect, { allowClear: false, style: { width: '100%' } }) }), _jsx(Form.Item, { name: "orderDate", label: "Order Date", children: _jsx(DatePicker, { showTime: false, format: "DD/MM/YYYY", allowClear: true }) }), _jsx(Form.Item, { name: "customerRef", label: "Customer Reference", rules: [{
                                                required: tenantSettings.consignmentCustomerRefMandatory,
                                                message: 'Please enter the customer reference'
                                            }, {
                                                max: 30,
                                                message: 'Please limit to 30 characters.'
                                            }], children: _jsx(Input, { maxLength: 30 }) }), _jsx(DivisionAccessFormItem, { required: true, tenantId: tenantId, name: "divisionId", allowMultiple: false, canBeRestricted: false })] }) }), _jsxs(Col, { lg: 12, xl: 8, children: [_jsx(Card, { title: "Pickup", type: "inner", children: _jsx(Form.Item, { name: "pickupDateTimeRange", label: "Pickup Date/Time", rules: [
                                            { validator: validatePickupBeforeDelivery }
                                        ], children: _jsx(DateRangePicker, {}) }) }), _jsxs(Card, { title: 'Delivery', type: 'inner', children: [_jsx(Form.Item, { name: "deliveryDateTimeRange", label: "Delivery Date/Time", rules: [{
                                                    required: true,
                                                    message: 'Please select a delivery window.'
                                                }], children: _jsx(DateRangePicker, {}) }), _jsx(CustomerDeliveryInstructionsFormItem, { form: form, customerId: selectedCustomer?.id })] })] }), _jsxs(Col, { lg: 12, xl: 8, children: [_jsx(Card, { title: 'Requirements', type: 'inner', children: _jsx(ConsignmentRequirementsFormItems, {}) }), tenantSettings.deliveryNotifications.enabled &&
                                    _jsx(Card, { title: 'Communications', type: 'inner', children: _jsx(ConsignmentCommunicationChannelsFormItems, {}) })] })] }) }), _jsx(Row, { children: _jsx(Col, { xs: 24, children: _jsx(Button, { className: "pull-right", type: "primary", htmlType: "button", size: "large", onClick: form.submit, children: "Next \u00BB" }) }) })] }));
};
export default React.forwardRef(CoreDetailsStep);
